import { useState, type ReactNode } from "react";

import type { TestCase } from "@/api/tests-api";
import type { CaseImageSource, SeriesRailState } from "@/components/tests/TestCaseViewer";
import { NO_FOLDS, ReadingRoom, type CaseNav, type RoomFolds } from "./ReadingRoom";

/**
 * THE APP'S IMAGE VIEWER, everywhere a case is read -- the reading room,
 * embedded in the host's own pane, with a "Full screen" that becomes the
 * whole room (decided 2026-10-07; docs/scope-reading-room.md section 11).
 *
 * It replaced `TestCaseViewer` (round 1's viewer) and `DiagnosticViewer`
 * (the screening round's) as a drop-in: same `source`, same series
 * contract, same staff-only fallback note. A case stored before diagnostic
 * files were kept still shows the old rendered-image viewer, silently for
 * a candidate.
 *
 * FULL SCREEN KEEPS THE SITTING IN VIEW. A candidate dictating -- or being
 * filmed -- cannot lose the recorder or the camera for the sake of a
 * bigger image, so the host passes `dockSide` and puts its own controls in
 * the dock with `Movable`, which moves them without remounting them.
 *
 * Keyed on the case: a new case is a fresh room. Full screen is held here,
 * above the key, so stepping case to case in full screen stays there.
 */
export function RoomViewer({
  source,
  testCase,
  showSeriesRail = true,
  explainFallback = false,
  onSeries,
  caseNav,
  dockSide,
  onDock,
  onFullScreen,
  allowFullScreen = true,
  full: fullProp,
  onFullChange,
  dockLabel,
  dockStrip,
  dockBar,
  onDockBar,
}: {
  source: CaseImageSource;
  testCase?: Pick<TestCase, "title" | "modality" | "clinical_history"> &
    Partial<Pick<TestCase, "body_part" | "patient_sex" | "patient_age" | "status">>;
  showSeriesRail?: boolean;
  explainFallback?: boolean;
  onSeries?: (state: SeriesRailState) => void;
  /** Previous / next case, shown in the full-screen banner. */
  caseNav?: CaseNav;
  dockSide?: "left" | "right";
  onDock?: (element: HTMLElement | null) => void;
  /** Take over the "Full screen" button -- for a host inside a Radix
   *  modal, whose focus trap and pointer lock a full-screen layer in the
   *  body cannot live under. */
  onFullScreen?: () => void;
  allowFullScreen?: boolean;
  /** Hold full screen in the HOST, for a page with its own button for it
   *  (round 1's "Focus"). Uncontrolled when absent. */
  full?: boolean;
  onFullChange?: (full: boolean) => void;
  dockLabel?: string;
  /** What the dock's folded strip keeps in sight. */
  dockStrip?: ReactNode;
  dockBar?: ReactNode;
  onDockBar?: (element: HTMLElement | null) => void;
}) {
  // Above the key, like full screen: a column folded on one case stays
  // folded on the next.
  const [folds, setFolds] = useState<RoomFolds>(NO_FOLDS);
  const [own, setOwn] = useState(false);
  const full = fullProp ?? own;
  const setFull = (next: boolean) => {
    setOwn(next);
    onFullChange?.(next);
  };
  const key = source.kind === "attempt" ? source.assignmentCaseId : source.caseId;
  const goFull = onFullScreen ?? (allowFullScreen ? () => setFull(true) : undefined);

  return (
    <ReadingRoom
      key={key}
      mode={full ? "full" : "embedded"}
      source={source}
      testCase={testCase ?? { title: "Case", modality: null, clinical_history: null }}
      showSeriesRail={showSeriesRail}
      explainFallback={explainFallback}
      onClose={() => setFull(false)}
      closeLabel="Exit full screen"
      {...(onSeries ? { onSeries } : {})}
      {...(full && caseNav ? { caseNav } : {})}
      {...(goFull ? { onFullScreen: goFull } : {})}
      {...(dockSide ? { dockSide } : {})}
      {...(onDock ? { onDock } : {})}
      {...(dockLabel ? { dockLabel } : {})}
      {...(dockStrip ? { dockStrip } : {})}
      {...(dockBar ? { dockBar } : {})}
      {...(onDockBar ? { onDockBar } : {})}
      folds={folds}
      onFoldsChange={setFolds}
    />
  );
}
